/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import isEmpty from 'lodash.isempty';
import isNil from 'lodash.isnull';
import {Account, GenericObject, ssoApps, SSOConfiguration} from '@ion/core/src/types/entities';
import {getKeyByValue} from '@ion/core/src/utils/ObjectUtil';
import {isValidURL} from '@ion/core/src/utils/Validators';
import {CTC_APPLICATIONS, GroupFormFields} from '@ion/dls/src/editors/GroupEditorUtils';
import {FormState} from '@ion/dls/src/hooks/useFormState';
import {SSOFieldCharacterLimits} from './SSOAppEntry';
import {Entry} from './SSOApps';
import {validateSSODomain} from './SSOValidator';

export enum SSOFormFields {
  DiscoveryEndpoint = 'discovery_endpoint',
  Domains = 'domains',
  SSOMode = 'sso_mode',
  APPs = 'apps',
  AppId = 'app_id',
  AppKey = 'app_key',
  ClientId = 'client_id',
  ClientSecret = 'client_secret',
  RedirectUri = 'redirect_uri',
  Scope = 'scope',
  AccountIds = 'account_ids'
};

export interface TargetType {
  name: SSOFormFields;
  value: unknown;
  required?: boolean;
  errText?: boolean;
};

export type SerializedArgs = {
  state: FormState;
  accountId?: number;
};

export const ssoConfigPermission = 'sso_config';

// initial form state values
const required    = {required: true};
const notRequired = {required: false};

// Builds the initial form state for the SSO Configuration form dialogs.
export const getInitialState = (ssoConfiguration: SSOConfiguration, selectedAccount: Account): FormState => {
  return ssoConfiguration?.id ? deserialize(ssoConfiguration, selectedAccount) : {
    [SSOFormFields.SSOMode]           : {...notRequired, value: 'LAX'},
    [SSOFormFields.DiscoveryEndpoint] : required,
    [SSOFormFields.Domains]           : required,
    [SSOFormFields.APPs]              : required
  };
};

export const getDomainValue = (domains: string | string[]) => {
  if (Array.isArray(domains)) {
    return domains;
  } else if (domains?.includes(',')) {
    return domains.split(',');
  } else {
    return [domains];
  }
};

// Serializes the form state into an API-consumable Account.
export const serialize = ({state, accountId}: SerializedArgs) => {
  const serialized = {};
  for (const [key, obj] of Object.entries(state)) {
    switch (key) {
      case SSOFormFields.Domains:
        serialized[key] = getDomainValue(obj.value);
        break;
      case SSOFormFields.APPs:
        serialized[key] = serializeSSOApps(obj.value);
        break;
      default:
        if (obj.value) {
          serialized[key] = obj.value;
        }
    }
  }
  serialized[SSOFormFields.AccountIds] = [accountId];
  return serialized;
};

/**
 * Deserializes a CTC SSOConfiguaration into an object which can be consumed by the SSO Editor Form
 * Fields and used to pre-populated their respective values.
 *
 * @param {SSOConfiguration} ssoConfiguration An optional CTC SSOConfiguaration instance
 * that is to be deserialized for the form.
 */
export const deserialize = (ssoConfiguration: SSOConfiguration, selectedAccount: Account): GenericObject => {
  if (ssoConfiguration) {
    return {
      [SSOFormFields.DiscoveryEndpoint] : {
        name  : SSOFormFields.DiscoveryEndpoint,
        value : ssoConfiguration[SSOFormFields.DiscoveryEndpoint]
      },
      [SSOFormFields.Domains] : {
        name  : SSOFormFields.Domains,
        value : ssoConfiguration[SSOFormFields.Domains]
      },
      [SSOFormFields.SSOMode] : {
        name  : SSOFormFields.SSOMode,
        value : selectedAccount[GroupFormFields.SSOMode]
      }
    };
  }
};

// This function validates the SSO Apps component
export const validateSSOApps = (appInfo: Entry[]) => {
  let isValid = true;
  appInfo?.forEach((item, ind) => {
    const app = item.apps;
    const {
      app_id,
      client_id,
      redirect_uri
    } = app;

    if (isEmpty(app_id)) {
      app.app_id_error = true;
      isValid          = false;
    } else {
      app.app_id_error = ind > 0 ? appNameExists(appInfo) : false;
      isValid          = isValid && !app.app_id_error;
    }

    if (client_id?.length > SSOFieldCharacterLimits.CLIENT_ID_MAX_LENGTH || isEmpty(client_id)) {
      app.client_id_error = true;
      isValid             = false;
    } else {
      app.client_id_error = false;
    }

    if (!isEmpty(redirect_uri) && !isValidURL(redirect_uri)) {
      app.redirect_uri_error = true;
      isValid                = false;
    } else {
      app.redirect_uri_error = false;
    }
  });
  return {
    appList : appInfo,
    isValid
  };
};

export const serializeSSOApps = (appInfo: Entry[]) => {
  const columns = [
    SSOFormFields.AppId,
    SSOFormFields.ClientId,
    SSOFormFields.ClientSecret,
    SSOFormFields.RedirectUri
  ];
  return appInfo?.map(({apps: app}) => {
    const appObj = {};
    columns.forEach(key => {
      if (key === SSOFormFields.AppId) {
        appObj[key] = CTC_APPLICATIONS[app[key]];
      } else {
        appObj[key] = app[key];
      }
    });
    appObj[SSOFormFields.Scope] = 'openid';
    return appObj;
  });
};

export const deserializeSSOApps = (ssoApps: ssoApps[]) => {
  const apps = [];
  ssoApps?.forEach((application, ind) => {
    const app = {
      ...application,
      [SSOFormFields.AppId]                   : getKeyByValue(CTC_APPLICATIONS, application?.app_id),
      [`${SSOFormFields.AppId}_error`]        : false,
      [`${SSOFormFields.ClientId}_error`]     : false,
      [`${SSOFormFields.ClientSecret}_error`] : false,
      [`${SSOFormFields.RedirectUri}_error`]  : false
    };
    apps.push({id: ind + 1, apps: app});
  });
  return apps;
};

export const appNameExists = (apps: GenericObject[]) => {
  const appMap = {};
  return apps?.some(({apps: app}) => {
    const {app_id: id} = app;
    if (appMap[id]) {
      return true;
    } else {
      appMap[id] = true;
    }
  }) || false;
};

export const setSSODomain = (val: string, setFormState): boolean => {
  const name    = SSOFormFields.Domains;
  const value   = val !== '' ? val : null;
  const errText = validateSSODomain(value);
  const error   = !isNil(errText);
  setFormState({
    [name] : {value, name, error, errText}
  });
  return error;
};
